import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, RotateCcw } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const LAYOUTS = [
  { id: "grid", labelKey: "visualizer.straight", w: 60, h: 60, offset: false },
  { id: "brick", labelKey: "visualizer.brick", w: 96, h: 48, offset: true },
  { id: "subway", labelKey: "visualizer.subway", w: 108, h: 44, offset: true },
  { id: "large", labelKey: "visualizer.largeFormat", w: 108, h: 108, offset: false },
];

const TILES = [
  { id: "white", labelKey: "visualizer.white", css: "#FAFAFA" },
  { id: "ivory", labelKey: "visualizer.ivory", css: "#F0EADD" },
  { id: "beige", labelKey: "visualizer.beige", css: "#E4D6BF" },
  { id: "grey", labelKey: "visualizer.grey", css: "#C4C4C0" },
  { id: "graphite", labelKey: "visualizer.graphite", css: "#6E6E6C" },
  { id: "black", labelKey: "visualizer.black", css: "#202024" },
  { id: "marble", labelKey: "visualizer.marble", css: "linear-gradient(135deg,#f7f6f3 0%,#e7e3db 45%,#f4f2ee 55%,#dcd7cd 100%)" },
];

const GROUTS = [
  { id: "bright-white", labelKey: "visualizer.brightWhite", css: "#F2F2EF", sparkle: false },
  { id: "ivory", labelKey: "visualizer.ivory", css: "#E9E1CE", sparkle: false },
  { id: "beige", labelKey: "visualizer.beige", css: "#CBB78F", sparkle: false },
  { id: "silver", labelKey: "visualizer.silverGrey", css: "#B4B4AF", sparkle: false },
  { id: "cement", labelKey: "visualizer.cementGrey", css: "#84847F", sparkle: false },
  { id: "charcoal", labelKey: "visualizer.charcoal", css: "#3C3C3A", sparkle: false },
  { id: "black", labelKey: "visualizer.jetBlack", css: "#161618", sparkle: false },
  { id: "chocolate", labelKey: "visualizer.chocolate", css: "#4A3524", sparkle: false },
  { id: "gold", labelKey: "visualizer.sparkleGold", css: "#8A6B1F", sparkle: "#E8C662" },
  { id: "silver-sp", labelKey: "visualizer.sparkleSilver", css: "#8C9096", sparkle: "#E3E7EC" },
  { id: "copper", labelKey: "visualizer.sparkleCopper", css: "#7A4326", sparkle: "#D98A54" },
];

const TILE_SIZES = [
  { id: "1x1", labelKey: "visualizer.size1x1", w: 1, h: 1 },
  { id: "1x1.5", labelKey: "visualizer.size1x15", w: 1, h: 1.5 },
  { id: "1x2", labelKey: "visualizer.size1x2", w: 1, h: 2 },
  { id: "2x1", labelKey: "visualizer.size2x1", w: 2, h: 1 },
  { id: "2x4", labelKey: "visualizer.size2x4", w: 2, h: 4 },
];

const FT_TO_PX = 72;
const DEFAULT_SIZE = TILE_SIZES[0];

const Field = ({ label, children }) => (
  <div>
    <label className="label mb-3 block text-muted-foreground">{label}</label>
    {children}
  </div>
);

const matchPreset = (w, h) => {
  const found = TILE_SIZES.find((s) => s.w === w && s.h === h);
  return found ? found.id : "custom";
};

export default function GroutVisualizer() {
  const { t } = useLanguage();
  const [layout, setLayout] = useState(LAYOUTS[1]);
  const [tile, setTile] = useState(TILES[0]);
  const [grout, setGrout] = useState(GROUTS[4]);
  const [joint, setJoint] = useState(4);
  const [tileW, setTileW] = useState(String(DEFAULT_SIZE.w));
  const [tileH, setTileH] = useState(String(DEFAULT_SIZE.h));
  const [sizePreset, setSizePreset] = useState(DEFAULT_SIZE.id);

  const gapPx = Math.max(2, Math.round(joint * 1.6));

  const previewW = useMemo(() => {
    const w = parseFloat(tileW);
    return Math.max(24, Math.round((Number.isFinite(w) && w > 0 ? w : DEFAULT_SIZE.w) * FT_TO_PX));
  }, [tileW]);

  const previewH = useMemo(() => {
    const h = parseFloat(tileH);
    return Math.max(24, Math.round((Number.isFinite(h) && h > 0 ? h : DEFAULT_SIZE.h) * FT_TO_PX));
  }, [tileH]);

  const rows = useMemo(() => {
    // enough tiles to overflow the preview panel; overflow hidden clips edges
    const count = layout.id === "large" ? 5 : 8;
    return Array.from({ length: count });
  }, [layout]);

  const cols = layout.id === "large" ? 8 : 12;

  const applyPreset = (id) => {
    const preset = TILE_SIZES.find((s) => s.id === id);
    if (!preset) return;
    setSizePreset(preset.id);
    setTileW(String(preset.w));
    setTileH(String(preset.h));
  };

  const onWidthChange = (value) => {
    setTileW(value);
    const w = parseFloat(value);
    const h = parseFloat(tileH);
    setSizePreset(Number.isFinite(w) && Number.isFinite(h) ? matchPreset(w, h) : "custom");
  };

  const onHeightChange = (value) => {
    setTileH(value);
    const w = parseFloat(tileW);
    const h = parseFloat(value);
    setSizePreset(Number.isFinite(w) && Number.isFinite(h) ? matchPreset(w, h) : "custom");
  };

  const reset = () => {
    setLayout(LAYOUTS[1]);
    setTile(TILES[0]);
    setGrout(GROUTS[4]);
    setJoint(4);
    setTileW(String(DEFAULT_SIZE.w));
    setTileH(String(DEFAULT_SIZE.h));
    setSizePreset(DEFAULT_SIZE.id);
  };

  const sparkleBg = grout.sparkle
    ? {
        backgroundColor: grout.css,
        backgroundImage: `radial-gradient(${grout.sparkle} 0.7px, transparent 0.9px)`,
        backgroundSize: "5px 5px",
      }
    : { backgroundColor: grout.css };

  const tileLabel = t(tile.labelKey);
  const groutLabel = t(grout.labelKey);
  const layoutLabel = t(layout.labelKey);

  return (
    <div className="grid gap-px bg-black/10 lg:grid-cols-[420px_1fr]" data-testid="grout-visualizer">
      {/* Controls */}
      <div className="space-y-8 bg-background p-8 md:p-12">
        <Field label={t("visualizer.tileLayout")}>
          <div className="grid grid-cols-2 gap-2" data-testid="viz-layouts">
            {LAYOUTS.map((l) => (
              <button
                key={l.id}
                onClick={() => setLayout(l)}
                data-testid={`viz-layout-${l.id}`}
                className={`border px-4 py-3 text-left text-sm font-display transition-colors duration-200 ${
                  layout.id === l.id ? "border-primary bg-primary text-primary-foreground" : "border-black/15 text-muted-foreground hover:text-foreground"
                }`}
              >
                {t(l.labelKey)}
              </button>
            ))}
          </div>
        </Field>

        <Field label={t("visualizer.tileColour")}>
          <div className="flex flex-wrap gap-2" data-testid="viz-tiles">
            {TILES.map((opt) => (
              <button
                key={opt.id}
                onClick={() => setTile(opt)}
                data-testid={`viz-tile-${opt.id}`}
                aria-label={t(opt.labelKey)}
                title={t(opt.labelKey)}
                className={`h-10 w-10 border transition-transform duration-200 hover:scale-105 ${
                  tile.id === opt.id ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "border-black/20"
                }`}
                style={{ background: opt.css }}
              />
            ))}
          </div>
          <p className="mt-2 text-xs text-black/40">{t("visualizer.selected")}: {tileLabel}</p>
        </Field>

        <Field label={t("visualizer.groutColour")}>
          <div className="flex flex-wrap gap-2" data-testid="viz-grouts">
            {GROUTS.map((g) => (
              <button
                key={g.id}
                onClick={() => setGrout(g)}
                data-testid={`viz-grout-${g.id}`}
                aria-label={t(g.labelKey)}
                title={t(g.labelKey)}
                className={`relative h-10 w-10 border transition-transform duration-200 hover:scale-105 ${
                  grout.id === g.id ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : "border-black/20"
                }`}
                style={{ background: g.css }}
              >
                {g.sparkle && <span className="absolute inset-0" style={{ backgroundImage: `radial-gradient(${g.sparkle} 0.8px, transparent 1px)`, backgroundSize: "4px 4px" }} />}
              </button>
            ))}
          </div>
          <p className="mt-2 text-xs text-black/40">
            {t("visualizer.selected")}: {groutLabel}{grout.sparkle ? t("visualizer.sparkleNote") : ""}
          </p>
        </Field>

        <Field label={`${t("visualizer.jointWidth")} — ${joint} mm`}>
          <input
            type="range"
            min="1"
            max="12"
            step="1"
            value={joint}
            onChange={(e) => setJoint(parseInt(e.target.value, 10))}
            data-testid="viz-joint-slider"
            className="h-1.5 w-full cursor-pointer appearance-none bg-black/15 accent-[hsl(var(--primary))]"
          />
          <div className="mt-2 flex justify-between text-xs text-black/40">
            <span>1 mm</span><span>12 mm</span>
          </div>
        </Field>

        <Field label={t("visualizer.tileSize")}>
          <select
            value={sizePreset === "custom" ? "custom" : sizePreset}
            onChange={(e) => {
              const id = e.target.value;
              if (id === "custom") {
                setSizePreset("custom");
                return;
              }
              applyPreset(id);
            }}
            data-testid="viz-tile-size-preset"
            className="h-12 w-full border border-black/15 bg-secondary/40 px-3 font-display text-sm outline-none focus:border-primary"
          >
            {TILE_SIZES.map((s) => (
              <option key={s.id} value={s.id}>{t(s.labelKey)}</option>
            ))}
            <option value="custom">{t("visualizer.custom")}</option>
          </select>
          <div className="mt-3 grid grid-cols-2 gap-3">
            <div>
              <span className="mb-2 block text-xs text-black/40">{t("visualizer.widthFt")}</span>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={tileW}
                onChange={(e) => onWidthChange(e.target.value)}
                data-testid="viz-tile-width"
                className="h-12 w-full border border-black/15 bg-secondary/40 px-3 font-mono text-lg outline-none focus:border-primary"
              />
            </div>
            <div>
              <span className="mb-2 block text-xs text-black/40">{t("visualizer.heightFt")}</span>
              <input
                type="number"
                min="0.1"
                step="0.1"
                value={tileH}
                onChange={(e) => onHeightChange(e.target.value)}
                data-testid="viz-tile-height"
                className="h-12 w-full border border-black/15 bg-secondary/40 px-3 font-mono text-lg outline-none focus:border-primary"
              />
            </div>
          </div>
        </Field>

        <button onClick={reset} data-testid="viz-reset" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <RotateCcw className="h-4 w-4" /> <span className="label">{t("visualizer.reset")}</span>
        </button>
      </div>

      {/* Preview */}
      <div className="min-w-0 overflow-hidden bg-secondary/20 p-8 md:p-12">
        <div className="flex items-center justify-between gap-4">
          <span className="label text-primary">{t("visualizer.livePreview")}</span>
          <span className="label shrink-0 text-muted-foreground">{layoutLabel} · {joint}mm · {tileW || DEFAULT_SIZE.w}×{tileH || DEFAULT_SIZE.h} ft</span>
        </div>

        <motion.div
          key={`${layout.id}-${previewW}-${previewH}`}
          initial={{ opacity: 0.4 }}
          animate={{ opacity: 1 }}
          className="mt-6 h-[320px] w-full max-w-full overflow-hidden border border-black/10 md:h-[460px]"
          style={{ ...sparkleBg, padding: gapPx }}
          data-testid="viz-preview"
        >
          {rows.map((_, r) => (
            <div
              key={r}
              className="flex"
              style={{
                gap: gapPx,
                marginTop: r === 0 ? 0 : gapPx,
                marginLeft: layout.offset && r % 2 === 1 ? -(previewW / 2) : 0,
              }}
            >
              {Array.from({ length: cols }).map((__, c) => (
                <div
                  key={c}
                  className="shrink-0"
                  style={{
                    width: previewW,
                    height: previewH,
                    background: tile.css,
                    boxShadow: tile.id === "marble" ? "inset 0 0 12px rgba(0,0,0,0.06)" : "none",
                  }}
                />
              ))}
            </div>
          ))}
        </motion.div>

        <div className="mt-6 grid w-full grid-cols-3 gap-px bg-black/10">
          {[
            { k: t("visualizer.tile"), v: tileLabel },
            { k: t("visualizer.grout"), v: groutLabel },
            { k: t("visualizer.joint"), v: `${joint} mm` },
          ].map((x) => (
            <div key={x.k} className="min-w-0 bg-background p-4">
              <div className="label text-muted-foreground">{x.k}</div>
              <div className="mt-1 truncate font-display text-sm">{x.v}</div>
            </div>
          ))}
        </div>

        <p className="mt-6 text-xs leading-relaxed text-black/40">
          {t("visualizer.disclaimer")}
        </p>
        <Link to="/products?cat=grouts" className="mt-4 inline-flex items-center gap-2 text-foreground hover:text-primary" data-testid="viz-view-grouts">
          <span className="label">{t("visualizer.shopGrouts")}</span>
          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
}
